import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";

interface Firm { id: string; name: string }

/**
 * Pick an existing company or type a new name. Returns either
 * { firmCompanyId } (existing) or { firmName } (created on save by the server).
 */
export function CoiFirmPicker({ value, onChange }: {
  value: { firmCompanyId?: string; firmName?: string };
  onChange: (v: { firmCompanyId?: string; firmName?: string }) => void;
}) {
  const [text, setText] = useState(value.firmName ?? "");
  const [open, setOpen] = useState(false);
  const { data: firms = [] } = useQuery<Firm[]>({
    queryKey: ["companies", "coi-category-options"],
    queryFn: async () => {
      const r = await fetch("/api/companies?category=COI", { credentials: "include" });
      return r.ok ? (await r.json()).map((f: any) => ({ id: f.id, name: f.name })) : [];
    },
  });
  const matches = (text ? firms.filter((f) => f.name.toLowerCase().includes(text.toLowerCase())) : firms).slice(0, 8);
  const chosen = value.firmCompanyId ? firms.find((f) => f.id === value.firmCompanyId) : null;
  const isNew = !!text && !firms.some((f) => f.name.toLowerCase() === text.toLowerCase());
  return (
    <div className="relative">
      <Input
        className="h-11" placeholder="Firm, e.g. McAfee & Taft" value={chosen ? chosen.name : text}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setText(e.target.value); onChange({ firmName: e.target.value }); }}
      />
      {open && (matches.length > 0 || isNew) && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md text-sm">
          {matches.map((f) => (
            <button key={f.id} type="button" className="block w-full text-left px-3 py-1.5 hover:bg-muted"
              onMouseDown={() => { onChange({ firmCompanyId: f.id }); setText(f.name); setOpen(false); }}>
              {f.name}
            </button>
          ))}
          {isNew && <div className="px-3 py-1.5 text-muted-foreground border-t">Create “{text}” as a new company on save</div>}
        </div>
      )}
    </div>
  );
}
